/**
 * RedeemBetaPage — handles two routes:
 *   - /invite/:token   (public, unauthenticated; validates the invite & prompts sign-in)
 *   - /redeem?token=…  (authenticated; performs the redemption)
 *
 * State machine (per ADR-038 Decision 5; expanded by F12.1 Path 2):
 *   data-invite-state ∈ {loading, auth-loading, valid, masks-match-confirm,
 *                        wrong-user, not-found, expired, already-redeemed}
 *   data-redeem-state ∈ {loading, success, already-self, already-other, email-mismatch, error}
 *
 * `loading`       — invite-token validation in progress
 * `auth-loading`  — token validated; Zustand auth state has not yet resolved.
 *                   Added in F12.1 to prevent the public CTA from rendering
 *                   under a loading session, which previously masked the
 *                   authenticated correct-user case on direct /invite/ navigates.
 *
 * Both attributes are STATIC LITERALS (one per JSX branch — never `data-X={var}`).
 * Open-redirect protection: validateRedirectPath (in @/utils/redirect-validation)
 * rejects any `?redirect=` value not matching the strict UUIDv4 path regexes
 * (per ADR-037 Decision 9).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

// ── Architectural exception: direct Supabase client import ────────────────────
// Per ADR-019, components and stores normally route through StorageAdapter +
// SupabaseRepository. The M58 invite flow is a narrow exception:
//   1. There is no service layer for beta invites (no `beta-invite-service.ts`);
//      a single page consumes two Edge Functions (validate + redeem).
//   2. The calls are page-scoped and read-only from the page's perspective —
//      they invoke Edge Functions that own the writes server-side.
//   3. Wrapping a one-page two-call flow in a service module adds indirection
//      without architectural benefit (matches the precedent set by
//      `BetaInvitesPage.tsx`, which carries the same justification block).
import { FunctionsHttpError } from '@supabase/supabase-js';

import { supabase } from '@/services/supabase';
import { useAuthStore } from '@/stores/auth-store';
import { validateRedirectPath } from '@/utils/redirect-validation';
import { maskEmail, masksMatch } from '@/utils/email-mask';
import type {
  InviteState,
  RedeemState,
  ValidateInviteResponse,
  RedeemInviteResponse,
} from '@/types/beta-invite';
import type { User } from '@/types/user';

// Re-export validateRedirectPath so existing test imports continue to work.
// Source of truth lives in @/utils/redirect-validation per consensus-reviewer C10.
// eslint-disable-next-line react-refresh/only-export-components -- deliberate back-compat re-export (C10); not a Fast Refresh concern
export { validateRedirectPath };

// ── UUIDv4 token validation ───────────────────────────────────────────────────

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function isValidToken(token: string | null | undefined): token is string {
  return typeof token === 'string' && UUID_RE.test(token);
}

// ── Page component ────────────────────────────────────────────────────────────

export function RedeemBetaPage() {
  const params = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const authStatus = useAuthStore((s) => s.status);
  const authUser = useAuthStore((s) => s.user);

  // Token comes from either the path param (/invite/:token) or the query param (/redeem?token=)
  const pathToken = params.token ?? null;
  const queryToken = searchParams.get('token');
  const token = pathToken ?? queryToken;

  // Mode: 'invite' = public landing (validate only), 'redeem' = post-auth (redeem flow).
  // Determined by which route shape supplied the token.
  const mode: 'invite' | 'redeem' = pathToken != null ? 'invite' : 'redeem';

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;

  // Initial state computed synchronously: invalid tokens (or missing supabase
  // URL on the invite path) land on the terminal state immediately. This avoids
  // setState-in-effect for the deterministic synchronous failure modes.
  const [inviteState, setInviteState] = useState<InviteState>(() => {
    if (mode !== 'invite') return 'loading';
    if (!isValidToken(token)) return 'not-found';
    if (!supabaseUrl) return 'not-found';
    return 'loading';
  });
  const [redeemState, setRedeemState] = useState<RedeemState>(() =>
    mode === 'redeem' && !isValidToken(token) ? 'error' : 'loading',
  );
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [betaExpiresAt, setBetaExpiresAt] = useState<string | null>(null);

  // Guard against double-fetch in React Strict Mode
  const validateRanRef = useRef(false);
  const redeemRanRef = useRef(false);

  // ── Invite landing flow: validate the token ─────────────────────────────────
  useEffect(() => {
    if (mode !== 'invite') return;
    if (validateRanRef.current) return;
    if (!isValidToken(token)) return; // already in terminal 'not-found' state
    if (!supabaseUrl) return; // already in terminal 'not-found' state
    validateRanRef.current = true;

    const url = `${supabaseUrl}/functions/v1/validate-beta-invite/${token}`;

    void (async () => {
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok && res.status !== 200) {
          // Edge Function reports state via 200 body; non-200 is treated as not-found.
          setInviteState('not-found');
          return;
        }
        const json = (await res.json()) as ValidateInviteResponse;
        // C9: validate-beta-invite returns `emailHint` (masked, e.g. "t***@example.com"),
        // present only on the `valid` branch. Older clients reading `email` are
        // updated to consume the masked hint here so the recipient can confirm
        // "this invite is for me" without seeing the full address.
        setInviteEmail(json.emailHint ?? null);
        setInviteState(json.status);
      } catch (err) {
        console.error('[RedeemBetaPage] validate-beta-invite failed:', err);
        setInviteState('not-found');
      }
    })();
  }, [mode, token, supabaseUrl]);

  // ── Redemption flow: call redeem-beta-invite ────────────────────────────────
  useEffect(() => {
    if (mode !== 'redeem') return;
    // Wait for auth state to resolve before attempting redemption.
    if (authStatus === 'loading') return;
    if (redeemRanRef.current) return;
    if (!isValidToken(token)) return; // already in terminal 'error' state

    if (authStatus !== 'authenticated' || !authUser) {
      // Unauthenticated user landed on /redeem directly — surface as error;
      // App.tsx routing should redirect via /auth?redirect=… in normal flow.
      redeemRanRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- early-return branch clears state before async path
      setRedeemState('error');
      return;
    }
    redeemRanRef.current = true;

    void (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('redeem-beta-invite', {
          body: { token },
        });
        if (error) {
          console.error('[RedeemBetaPage] redeem-beta-invite error:', error);
          // supabase-js does NOT reject on a non-2xx response — it resolves
          // { data: null, error } where `error` is a FunctionsHttpError whose
          // `.context` is the raw Response (FunctionsClient.ts:296-298). The
          // server returns HTTP 403 { status: 'email-mismatch' } when the
          // signed-in account differs from the invited address
          // (redeem-beta-invite/handler.ts:185). Detect that case here so the
          // dedicated email-mismatch screen renders instead of the generic
          // error screen. The `instanceof FunctionsHttpError` narrow degrades
          // safely: non-HTTP subtypes (e.g. FunctionsFetchError) lack
          // `.context.status` and fall through to the generic 'error' state.
          if (error instanceof FunctionsHttpError && error.context.status === 403) {
            try {
              const body = (await error.context.json()) as { status?: string };
              if (body?.status === 'email-mismatch') {
                setRedeemState('email-mismatch');
                return;
              }
            } catch (parseErr) {
              console.error(
                '[RedeemBetaPage] failed to parse redeem-beta-invite 403 body:',
                parseErr,
              );
            }
          }
          setRedeemState('error');
          return;
        }
        const response = data as RedeemInviteResponse;
        setBetaExpiresAt(response.betaExpiresAt ?? null);
        setRedeemState(response.status);
      } catch (err) {
        console.error('[RedeemBetaPage] redeem-beta-invite failed:', err);
        setRedeemState('error');
      }
    })();
  }, [mode, token, authStatus, authUser]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (mode === 'invite') {
    return renderInviteLanding({
      inviteState,
      inviteEmail,
      token,
      authStatus,
      authUser,
      signOut: () => { void signOut(); },
      navigate,
    });
  }

  return renderRedeemFlow({
    redeemState,
    betaExpiresAt,
    inviteEmail,
    currentUserEmail: authUser?.email ?? null,
    onSignOut: () => {
      void signOut();
    },
  });
}

// ── Invite landing render branches (data-invite-state) ────────────────────────

interface InviteLandingArgs {
  inviteState: InviteState;
  inviteEmail: string | null;
  token: string | null;
  authStatus: 'loading' | 'authenticated' | 'unauthenticated';
  authUser: User | null;
  signOut: () => void;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}

function renderInviteLanding({
  inviteState,
  inviteEmail,
  token,
  authStatus,
  authUser,
  signOut,
  navigate,
}: InviteLandingArgs): JSX.Element {
  const containerClass =
    'min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12';
  const cardClass =
    'w-full max-w-sm bg-bark-800 border-[0.5px] border-bark-600 rounded-xl shadow-card-lg backdrop-blur-sm p-6 flex flex-col gap-4 text-center';

  /*
    (i)  Gate condition verbatim:
         /invite: inviteState !== 'loading' && inviteState !== 'auth-loading'
         /redeem: redeemState !== 'loading'

    (ii) useEffect-alignment interpretation:
         The state machine settles on a terminal branch when the async effect resolves.
         The route-ready wrapper becomes present only when the state machine has reached
         a terminal (non-loading) state, at which point the redemption useEffect has
         either completed or been bypassed. Tests that gate on route-ready therefore
         observe a stable, post-effect state.

    (iii) Layout-neutrality rationale:
          The wrapper uses Tailwind v4's `contents` utility (display: contents) so it sits in
          the DOM for Playwright querying but does not participate in the CSS box tree,
          preserving the inner element's direct-child relationship to its parent and protecting
          iOS Safari PWA viewport-recalc defenses.

    (iv) Optional contract callouts:
         Coexistence with data-invite-state/data-redeem-state per ADR-038 Decision 5:
         the route-ready wrapper is additive — existing attributes remain on their current
         elements, and the wrapper is inserted at the appropriate container level to make
         the sentinel queryable by Playwright without displacing the state-machine attributes.
         Static-literal-per-branch invariant: each render branch produces exactly one
         static literal value for the state attribute; no dynamic derivation (`data-X={var}`)
         is permitted. This invariant is enforced at review.

    PRESCRIPTIVE inline label (verbatim, ADR-042.1 Section 6):
    PRESCRIPTIVE for route-ready wrapper integration — the state-machine attributes
    (data-invite-state, data-redeem-state) exist in source per ADR-038 Decision 5;
    the route-ready wrapper alongside them does not yet exist and is implemented by
    the Wave 1 retrofit session.
  */

  if (inviteState === 'loading') {
    return (
      <div className={containerClass} data-invite-state="loading">
        <div className={cardClass}>
          <div className="w-8 h-8 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin self-center" />
          <p className="text-body-sm text-[var(--color-text-muted)]">
            Checking your invite…
          </p>
        </div>
      </div>
    );
  }

  if (inviteState === 'valid') {
    const redeemPath = token != null ? `/redeem?token=${token}` : '/';
    const authHref = `/auth?redirect=${encodeURIComponent(redeemPath)}${
      inviteEmail ? `&email=${encodeURIComponent(inviteEmail)}` : ''
    }`;

    // ── F12.1 Path 2: auth still loading — block the public CTA ──────────────
    // When a user navigates directly to /invite/:token while authenticated, the
    // Zustand store starts in `status: 'loading'` until <AuthBootstrap />'s
    // initialize() resolves. Rendering the public sign-in CTA in that window
    // masks the eventual authenticated-user branches (masks-match-confirm or
    // wrong-user). Hold the page on a dedicated `auth-loading` state instead.
    if (authStatus === 'loading') {
      return (
        <div className={containerClass} data-invite-state="auth-loading">
          <div className={cardClass}>
            <div className="w-8 h-8 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin self-center" />
            <p className="text-body-sm text-[var(--color-text-muted)]">
              Checking your sign-in&hellip;
            </p>
          </div>
        </div>
      );
    }

    // ── Mode B: authenticated user on /invite — detect wrong-user or masks-match ──
    // Auth has resolved. If the user is signed in, branch on mask comparison.
    // Otherwise, fall through to the public CTA below.
    if (authStatus === 'authenticated' && authUser != null) {
      const currentMask = maskEmail(authUser.email);
      const inviteMaskMatch = masksMatch(currentMask, inviteEmail);

      if (inviteMaskMatch) {
        // Masks match: prompt the authenticated user to confirm and continue
        // to /redeem — they're signed in as the likely intended recipient.
        return (
          <div className="contents" data-testid="route-ready">
          <div className={containerClass} data-invite-state="masks-match-confirm">
            <div className={cardClass}>
              <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
                Confirm this is your invite
              </h1>
              {inviteEmail && (
                <p className="text-body-sm text-[var(--color-text-muted)]">
                  This invite is for{' '}
                  <span className="font-medium">{inviteEmail}</span>.
                </p>
              )}
              <p className="text-body-sm text-[var(--color-text-muted)]">
                You&rsquo;re signed in as{' '}
                <span className="font-medium">{authUser.email}</span>.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => navigate(redeemPath, { replace: false })}
                  className="inline-flex items-center justify-center w-full py-2.5 rounded-md bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
                  data-testid="invite-masks-match-continue-cta"
                >
                  Continue
                </button>
                <button
                  type="button"
                  onClick={signOut}
                  className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
                  data-testid="invite-masks-match-signout-cta"
                >
                  Sign out and use a different account
                </button>
              </div>
            </div>
          </div>
          </div>
        );
      }

      // Masks differ: authenticated wrong-user — do not attempt redemption.
      // Server-side email-mismatch 403 remains the source of truth for the
      // rare same-mask false-positive case.
      return (
        <div className="contents" data-testid="route-ready">
        <div className={containerClass} data-invite-state="wrong-user">
          <div className={cardClass}>
            <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
              This invite is for a different account
            </h1>
            {inviteEmail && (
              <p className="text-body-sm text-[var(--color-text-muted)]">
                Your invite was sent to{' '}
                <span className="font-medium">{inviteEmail}</span>,
                but you&rsquo;re signed in as{' '}
                <span className="font-medium">{authUser.email}</span>.
                Sign out and sign in with the invited address.
              </p>
            )}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={signOut}
                className="inline-flex items-center justify-center w-full py-2.5 rounded-md bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
                data-testid="invite-wrong-user-signout-cta"
              >
                Sign out
              </button>
              <Link
                to="/"
                className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
              >
                Cancel
              </Link>
            </div>
          </div>
        </div>
        </div>
      );
    }

    // Unauthenticated or auth still loading: show public sign-in CTA.
    return (
      <div className="contents" data-testid="route-ready">
      <div className={containerClass} data-invite-state="valid">
        <div className={cardClass}>
          <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
            You&rsquo;re invited to Ridgen Beta
          </h1>
          {inviteEmail && (
            <p className="text-body-sm text-[var(--color-text-muted)]">
              This invite is for <span className="font-medium">{inviteEmail}</span>.
              Sign in with that email to redeem.
            </p>
          )}
          <Link
            to={authHref}
            className="inline-flex items-center justify-center w-full py-2.5 rounded-md bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
            data-testid="invite-signin-cta"
          >
            Sign in to redeem
          </Link>
        </div>
      </div>
      </div>
    );
  }

  if (inviteState === 'not-found') {
    return (
      <div className="contents" data-testid="route-ready">
      <div className={containerClass} data-invite-state="not-found">
        <div className={cardClass}>
          <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
            Invite not found
          </h1>
          <p className="text-body-sm text-[var(--color-text-muted)]">
            We couldn&rsquo;t find this invite. It may have been mistyped or revoked.
          </p>
          <Link
            to="/"
            className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
          >
            Go to Ridgen
          </Link>
        </div>
      </div>
      </div>
    );
  }

  if (inviteState === 'expired') {
    return (
      <div className="contents" data-testid="route-ready">
      <div className={containerClass} data-invite-state="expired">
        <div className={cardClass}>
          <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
            This invite has expired
          </h1>
          <p className="text-body-sm text-[var(--color-text-muted)]">
            Beta invites are valid for 48 hours. Reach out for a fresh link.
          </p>
          <Link
            to="/"
            className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
          >
            Go to Ridgen
          </Link>
        </div>
      </div>
      </div>
    );
  }

  // inviteState === 'already-redeemed'
  return (
    <div className="contents" data-testid="route-ready">
    <div className={containerClass} data-invite-state="already-redeemed">
      <div className={cardClass}>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          This invite has already been redeemed
        </h1>
        <p className="text-body-sm text-[var(--color-text-muted)]">
          If this is your invite, sign in and visit your subscription page.
        </p>
        <Link
          to="/"
          className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
        >
          Go to Ridgen
        </Link>
      </div>
    </div>
    </div>
  );
}

// ── Redeem flow render branches (data-redeem-state) ───────────────────────────

interface RedeemFlowArgs {
  redeemState: RedeemState;
  betaExpiresAt: string | null;
  inviteEmail: string | null;
  currentUserEmail: string | null;
  onSignOut: () => void;
}

function renderRedeemFlow({
  redeemState,
  betaExpiresAt,
  inviteEmail,
  currentUserEmail,
  onSignOut,
}: RedeemFlowArgs): JSX.Element {
  const containerClass =
    'min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12';
  const cardClass =
    'w-full max-w-sm bg-bark-800 border-[0.5px] border-bark-600 rounded-xl shadow-card-lg backdrop-blur-sm p-6 flex flex-col gap-4 text-center';

  if (redeemState === 'loading') {
    return (
      <div className={containerClass}>
        <div className={cardClass} data-redeem-state="loading">
          <div className="w-8 h-8 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin self-center" />
          <p className="text-body-sm text-[var(--color-text-muted)]">
            Redeeming your invite…
          </p>
        </div>
      </div>
    );
  }

  if (redeemState === 'success') {
    return (
      <div className="contents" data-testid="route-ready">
      <div className={containerClass}>
        <div className={cardClass} data-redeem-state="success">
          <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
            You&rsquo;re in!
          </h1>
          <p className="text-body-sm text-[var(--color-text-muted)]">
            {betaExpiresAt
              ? `Beta Pro is active until ${formatExpiry(betaExpiresAt)}.`
              : 'Beta Pro is active on your account.'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full py-2.5 rounded-md bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
            data-testid="redeem-get-started-cta"
          >
            Get started
          </Link>
        </div>
      </div>
      </div>
    );
  }

  if (redeemState === 'already-self') {
    return (
      <div className="contents" data-testid="route-ready">
      <div className={containerClass}>
        <div className={cardClass} data-redeem-state="already-self">
          <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
            You&rsquo;re already in.
          </h1>
          <p className="text-body-sm text-[var(--color-text-muted)]">
            {betaExpiresAt
              ? `This invite is active on your account. Beta Pro is active until ${formatExpiry(betaExpiresAt)}.`
              : 'This invite is active on your account.'}
          </p>
          <div className="flex flex-col gap-2">
            <Link
              to="/"
              className="inline-flex items-center justify-center w-full py-2.5 rounded-md bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
            >
              Open dashboard
            </Link>
            <Link
              to="/subscription"
              className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
            >
              View subscription
            </Link>
          </div>
        </div>
      </div>
      </div>
    );
  }

  if (redeemState === 'already-other') {
    return (
      <div className="contents" data-testid="route-ready">
      <div className={containerClass}>
        <div className={cardClass} data-redeem-state="already-other">
          <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
            This invite is already in use.
          </h1>
          <p className="text-body-sm text-[var(--color-text-muted)]">
            Someone has already redeemed this link. If you believe this is a mistake, get
            in touch.
          </p>
          <div className="flex flex-col gap-2">
            <a
              href="mailto:hello@ridgen.app"
              className="inline-flex items-center justify-center w-full py-2.5 rounded-md bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
            >
              Email us
            </a>
            <Link
              to="/"
              className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
            >
              Go to Ridgen
            </Link>
          </div>
        </div>
      </div>
      </div>
    );
  }

  if (redeemState === 'email-mismatch') {
    return (
      <div className="contents" data-testid="route-ready">
      <div className={containerClass}>
        <div className={cardClass} data-redeem-state="email-mismatch">
          <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
            This invite is for a different account.
          </h1>
          <p className="text-body-sm text-[var(--color-text-muted)]">
            Your invite was sent to{' '}
            <span className="font-medium" data-testid="redeem-mismatch-invited-email">{inviteEmail ?? 'a different address'}</span>,
            but you&rsquo;re signed in as{' '}
            <span className="font-medium" data-testid="redeem-mismatch-current-email">{currentUserEmail ?? 'an unknown account'}</span>.
            Sign out and sign in with the invited address.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onSignOut}
              className="inline-flex items-center justify-center w-full py-2.5 rounded-md bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
              data-testid="redeem-signout-cta"
            >
              Sign out
            </button>
            <Link
              to="/"
              className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>
      </div>
    );
  }

  // redeemState === 'error'
  return (
    <div className="contents" data-testid="route-ready">
    <div className={containerClass}>
      <div className={cardClass} data-redeem-state="error">
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          Something went wrong.
        </h1>
        <p className="text-body-sm text-[var(--color-text-muted)]">
          We couldn&rsquo;t redeem your invite. Try again, or get in touch if the problem
          persists.
        </p>
        <Link
          to="/"
          className="text-body-sm text-[var(--color-accent)] hover:opacity-80"
        >
          Go to Ridgen
        </Link>
      </div>
    </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatExpiry(iso: string): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
